import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { closeButtonClasses } from "./dialog"

/**
 * Боковая панель (drawer) для карточек деталей — по макету «Карточка пользователя»:
 * справа, 520px, фон bg, шапка 64px на surface, прокручиваемое тело.
 *
 *   <Sheet open={!!id} onOpenChange={(o) => !o && setId(null)}>
 *     <SheetContent>
 *       <SheetHeader title="Карточка пользователя" actions={<Button size="icon-sm" …/>} />
 *       <SheetBody>…</SheetBody>
 *       <SheetFooter>…</SheetFooter>
 *     </SheetContent>
 *   </Sheet>
 */
const Sheet = DialogPrimitive.Root
const SheetTrigger = DialogPrimitive.Trigger
const SheetClose = DialogPrimitive.Close
const SheetPortal = DialogPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-hero/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-black/60",
      className
    )}
    {...props} />
))
SheetOverlay.displayName = "SheetOverlay"

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: "right" | "left"
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, side = "right", children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-y-0 z-50 flex w-[520px] max-w-full flex-col bg-bg text-text shadow-pop outline-none duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
        side === "right"
          ? "right-0 border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          : "left-0 border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        className
      )}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = "SheetContent"

export interface SheetHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode
  /** Кнопки слева от «Закрыть» (например «Открыть на отдельной странице»). */
  actions?: React.ReactNode
  closeLabel?: string
}

const SheetHeader = ({ title, actions, closeLabel = "Закрыть", className, ...props }: SheetHeaderProps) => (
  <div
    className={cn(
      "flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border-soft bg-surface px-5",
      className
    )}
    {...props}>
    <DialogPrimitive.Title className="truncate font-display text-lg font-bold">{title}</DialogPrimitive.Title>
    <div className="flex items-center gap-2">
      {actions}
      <DialogPrimitive.Close className={closeButtonClasses} aria-label={closeLabel}>
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
    </div>
  </div>
)
SheetHeader.displayName = "SheetHeader"

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-[18px]", className)} {...props} />
)
SheetBody.displayName = "SheetBody"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-border-soft bg-surface px-5 py-3.5", className)}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-text-2", className)} {...props} />
))
SheetDescription.displayName = "SheetDescription"

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetDescription,
}
